/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the CategoryListItem component that is displayed at the top of each
 * view.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

/**
 * Utility used to generate a Hook to allow for the creation of styles using JSS.
 */
import { makeStyles } from '@material-ui/core/styles';

/**
 * Material UI ListItem component.
 */
import ListItem from '@material-ui/core/ListItem';

/**
 * Material UI ListItemIcon component.
 */
import ListItemIcon from '@material-ui/core/ListItemIcon';

/**
 * Material UI ListItemText component.
 */
import ListItemText from '@material-ui/core/ListItemText';

/**
 * Material UI Fade component.
 */
import Fade from '@material-ui/core/Fade';

/**
 * Material UI Divider component.
 */
import Divider from '@material-ui/core/Divider';

/**
 * Material UI Grid component.
 */
import Grid from '@material-ui/core/Grid';

/**
 * Material UI Typography component.
 */
import { Typography } from '@material-ui/core';

/**
 * Used to display SVG icons.
 */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

/**
 * SVG icon definition
 */
import { faFilesMedical } from '@fortawesome/pro-solid-svg-icons/faFilesMedical';

/**
 * used to navigate around the app.
 */
import { Link as RouterLink } from 'react-router-dom';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/**
 * Enables the use of category data loaded from the ServiceStack API.
 */
import useCategoriesIndividual from 'src/hooks/useCategoriesIndividual';

/**
 * Typings
 */
import * as Dtos from '../api/dtos';
import SearchHighlighter from './SearchHighlighter';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces / Types
 * ---------------------------------------------------------------------------------
 */

interface ICategoryListItemProps {
    categoryId: number;
    version: Dtos.Version
    showDivider?: boolean;
    getCategory?: (versionId: number, categoryId: number) => [Dtos.Category | null];
    search?: string;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles(theme => ({
    listItem: {
    },
}));

const useGridStyles = makeStyles(theme => ({
    gridRow: {
        width: 'auto',
        padding: theme.spacing(1, 0),
        borderBottom: `1px solid rgba(0, 0, 0, 0.12)`,
        // marginRight: -theme.spacing(2),

        '&:first-child': {
            paddingTop: 0
        },

        '&:last-child': {
            borderBottom: 'none'
        }
    },
    gridTitle: {

    },
    gridValue: {
        alignSelf: 'center'
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const GradeListItemGridRow: React.FunctionComponent<{label: string; value: React.ReactNode; }> = ({
    label,
    value
}) => {
    const classes = useGridStyles();

    return (
        <Grid
            container
            className={classes.gridRow}
        >
            <Grid
                item
                className={classes.gridTitle}
                xs={12}
                sm={2}
                md={2}
                lg={2}
                xl={1}
            >
                <Typography
                    variant="h6"
                >
                    {label}
                </Typography>
            </Grid>
            <Grid
                item
                xs={12}
                sm={10}
                md={10}
                lg={10}
                xl={11}
                className={classes.gridValue}
            >
                {value}
            </Grid>
        </Grid>
    )
}

const CategoryListItem: React.FunctionComponent<ICategoryListItemProps> = ({
    categoryId,
    version,
    showDivider,
    getCategory,
    search
}) => {
    // Get classnames for styles.
    const classes = useStyles();

    const [category] = getCategory ?
        getCategory(version.id, categoryId) :
        useCategoriesIndividual(version.id, categoryId);

    return (
        <Fade
            in={true}
        >
            <div>
                <ListItem
                    button
                    className={classes.listItem}
                    component={RouterLink}
                    to={`/${version.code}/browse/category/${categoryId}`}
                >
                    <ListItemIcon>
                        <FontAwesomeIcon fixedWidth icon={faFilesMedical} size="lg" />
                    </ListItemIcon>
                    <ListItemText
                        primary={
                            category ?
                                search ?
                                    <GradeListItemGridRow
                                        label="Category"
                                        value={
                                            <Typography>
                                                <SearchHighlighter
                                                    searchTerms={search}
                                                >
                                                    {category.name}
                                                </SearchHighlighter>
                                            </Typography>
                                        }
                                    /> :
                                    category.name :
                                ''
                        }
                    />
                </ListItem>
                {
                    showDivider && (
                        <Divider />
                    )
                }
            </div>
        </Fade>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Export - Default
 * ---------------------------------------------------------------------------------
 */

export default CategoryListItem;


// WEBPACK FOOTER //
// ./src/components/CategoryListItem.tsx